/**
 * Record-evacuation-order modal (#445-(3)) — opened after the operator draws the
 * evacuation AREA. Captures the level (ALERT/ORDER) + the MANDATORY structured
 * issuing authority (type + named body) + an optional area name / population / notes,
 * then declares the order.
 *
 * HONESTY (§G Municipal BLOCKING correction): the authority is bound INTO the primary
 * action label — the header AND the submit button read "Record evacuation order —
 * issued by <authority>" as ONE phrase (NOT "Declare Evacuation Order" with a
 * detached "the COP doesn't confer this" footnote). The COP RECORDS / coordinates the
 * order on behalf of the named authority; it never itself confers the legal power to
 * evacuate. Cloned from CommunityEntityForm (the full-modal-with-dropdown idiom).
 */
import { useEffect, useState } from 'react';
import {
  ISSUING_AUTHORITY_TYPES,
  ISSUING_AUTHORITY_TYPE_LABELS,
  isFirstNationEvacuation,
  FN_EVAC_REQUEST_ACTION_LABEL,
  type CommunityEvacZoneSource,
  type EvacOrderLevel,
  type IssuingAuthorityType,
  type OperatorEvacOrderFeature,
} from '@tsr/shared-types';
import { declareEvacuationOrder } from './api';
import {
  COORDINATING_AUTHORITY_OPTIONAL_LABEL,
  COORDINATING_BODY_NAMED_LABEL,
} from './incidentVocabulary';
import { getSessionId } from './lib/operatorActions';
import { showToast } from './lib/toast';

export interface EvacuationOrderFormPreseed {
  /** The community this was launched from (pre-seeds community_id + the named body). */
  communityId?: string;
  /** Default named issuing body (e.g. the community name) — editable. */
  issuingAuthorityName?: string;
  /** Default issuing-authority type (derived from the community type). */
  issuingAuthorityType?: IssuingAuthorityType;
  /** Optional incident linkage. */
  incidentId?: string;
}

/**
 * #457 — provenance of a GENERATED (one-click) evacuation zone. Present only for
 * the "evacuate this community" path; absent for a hand-drawn ring.
 */
export interface EvacuationZoneProvenance {
  source: CommunityEvacZoneSource;
  /** For `populated_place`: the matched StatCan boundary name. */
  placeName?: string | null;
  /** For `radius`: the buffer radius in metres. */
  radiusM?: number | null;
}

export interface EvacuationOrderFormProps {
  /** The closed GeoJSON ring — drawn OR generated from the community (#457). */
  ring: Array<[number, number]>;
  preseed?: EvacuationOrderFormPreseed;
  /** #457 — set when the ring was auto-generated (drives the provenance banner). */
  geometrySource?: EvacuationZoneProvenance;
  /** #457 — swap the generated zone for a hand-drawn one (opens the draw tool). */
  onRedraw?: () => void;
  onClose: () => void;
  onSubmitted: (feature: OperatorEvacOrderFeature) => void;
}

/** Human phrase for where a generated evacuation zone came from. */
function provenanceLabel(g: EvacuationZoneProvenance): string {
  switch (g.source) {
    case 'service_area':
      return "the community's service-area boundary";
    case 'populated_place':
      return g.placeName
        ? `the StatCan community boundary (${g.placeName})`
        : 'the StatCan community boundary';
    case 'radius': {
      const km = (g.radiusM ?? 0) / 1000;
      const pretty = Number.isInteger(km) ? String(km) : km.toFixed(1);
      return `a ${pretty} km radius around the community centre (no boundary on file)`;
    }
    default: {
      // Exhaustiveness guard — a new CommunityEvacZoneSource must add a label here.
      const _exhaustive: never = g.source;
      return _exhaustive;
    }
  }
}

const LEVEL_OPTIONS: { value: EvacOrderLevel; label: string }[] = [
  { value: 'alert', label: 'ALERT — be ready to leave' },
  { value: 'order', label: 'ORDER — mandatory leave now' },
];

/** Parse an optional non-negative integer field. '' → undefined (omit); bad → null. */
function parsePop(v: string): number | null | undefined {
  if (v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isInteger(n) && n >= 0 ? n : null;
}

export function EvacuationOrderForm({
  ring,
  preseed,
  geometrySource,
  onRedraw,
  onClose,
  onSubmitted,
}: EvacuationOrderFormProps): JSX.Element {
  const [level, setLevel] = useState<EvacOrderLevel>('alert');
  const [authorityType, setAuthorityType] = useState<IssuingAuthorityType>(
    preseed?.issuingAuthorityType ?? 'municipal',
  );
  const [authorityName, setAuthorityName] = useState(preseed?.issuingAuthorityName ?? '');
  // #458 — OPTIONAL coordinating authority (the Far-North tripartite case: a Band ISSUES,
  // the PEOC COORDINATES). Secondary to the issuer; blank name → no coordinator recorded.
  const [coordType, setCoordType] = useState<IssuingAuthorityType>('provincial');
  const [coordName, setCoordName] = useState('');
  const [name, setName] = useState('');
  const [population, setPopulation] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // #637 — a First Nation evacuation is a REQUEST to the PEOC (ISC included), NOT an order
  // issued by an authority: no issuer, no alert/order register, no "order" token. The whole
  // form reframes off the LIVE authority-type (so switching the dropdown to First Nation
  // switches the form to request-mode). Municipal / provincial / federal are UNCHANGED.
  const isFirstNation = isFirstNationEvacuation(authorityType);
  // On a reserve there is no alert/order register — the request is recorded at the ALERT
  // register (the server rejects a first_nation `order`); the selector is hidden for FN.
  const submitLevel: EvacOrderLevel = isFirstNation ? 'alert' : level;

  // The authority is bound INTO the action label (header + submit) — never a footnote.
  const authorityDisplay = authorityName.trim() || '…';
  const actionLabel = isFirstNation
    ? FN_EVAC_REQUEST_ACTION_LABEL
    : `Record evacuation order — issued by ${authorityDisplay}`;

  const submit = (): void => {
    if (busy) return;
    if (authorityName.trim().length === 0) {
      showToast(
        isFirstNation
          ? 'Name the First Nation community whose evacuation is requested.'
          : 'Name the issuing authority — the COP records the order on its behalf.',
        { variant: 'error' },
      );
      return;
    }
    const pop = parsePop(population);
    if (pop === null) {
      showToast('Population estimate must be a non-negative whole number.', { variant: 'error' });
      return;
    }

    setBusy(true);
    void declareEvacuationOrder({
      session_id: getSessionId(),
      level: submitLevel,
      geometry: { type: 'Polygon', coordinates: [ring] },
      issuing_authority_type: authorityType,
      issuing_authority_name: authorityName.trim(),
      // #637 — no coordinating authority on the First Nation path (the PEOC-coordinates /
      // ISC-included model is baked into the request framing, not an optional add-on).
      coordinating_authority_type: !isFirstNation && coordName.trim() ? coordType : undefined,
      coordinating_authority_name: !isFirstNation && coordName.trim() ? coordName.trim() : undefined,
      name: name.trim() || undefined,
      community_id: preseed?.communityId,
      incident_id: preseed?.incidentId,
      population_estimate: pop,
      notes: notes.trim() || undefined,
    })
      .then((feature) => {
        showToast(
          isFirstNation
            ? 'Evacuation request recorded — submitted to the PEOC (ISC included).'
            : `Evacuation ${submitLevel} recorded — issued by ${authorityName.trim()}.`,
          { variant: 'success' },
        );
        onSubmitted(feature);
      })
      .catch((err) =>
        showToast(
          (err as Error).message ||
            (isFirstNation
              ? 'Could not record the evacuation request. Please retry.'
              : 'Could not record the evacuation order. Please retry.'),
          { variant: 'error' },
        ),
      )
      .finally(() => setBusy(false));
  };

  const inputCls =
    'w-full text-xs bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-slate-100 placeholder-slate-500';

  return (
    <div
      data-testid="evac-order-form-backdrop"
      onClick={onClose}
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-6"
    >
      <div
        data-testid="evac-order-form-modal"
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col w-full max-w-md max-h-[88vh] rounded-lg border border-violet-700/60 bg-slate-900 text-slate-100 shadow-2xl"
      >
        <header className="flex items-start justify-between px-5 py-3 border-b border-slate-700">
          <h2 className="text-sm font-semibold tracking-tight pr-2" data-testid="evac-order-form-title">
            {isFirstNation ? (
              <>
                Record evacuation request —{' '}
                <span className="text-violet-300">submitted to PEOC (ISC included)</span>
              </>
            ) : (
              <>
                Record evacuation order —{' '}
                <span className="text-violet-300">issued by {authorityDisplay}</span>
              </>
            )}
          </h2>
          <button
            type="button"
            data-testid="evac-order-form-close"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-100 text-lg leading-none px-1"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          {geometrySource && (
            <div
              data-testid="evac-zone-provenance"
              className="rounded border border-violet-700/50 bg-violet-950/40 px-2.5 py-2 text-[11px] text-violet-200 space-y-1.5"
            >
              <p>
                Evacuation area covers the whole community — generated from{' '}
                {provenanceLabel(geometrySource)}. Review and adjust before issuing.
              </p>
              {onRedraw && (
                <button
                  type="button"
                  data-testid="evac-zone-redraw"
                  onClick={onRedraw}
                  className="text-[11px] py-1 px-2 rounded bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-200"
                >
                  Redraw the area manually
                </button>
              )}
            </div>
          )}
          <p className="text-[11px] text-slate-400">
            {isFirstNation
              ? 'The First Nation requests evacuation and the PEOC coordinates the response (ISC included). ' +
                'The COP records the request; there is no single issuer on a reserve — the decision is a ' +
                'Joint Command Call (Chief and Council with ISC).'
              : 'The COP records this order on behalf of the named authority and coordinates it on the map — ' +
                'it does not itself confer the legal power to evacuate.'}
          </p>

          {/* #637 — the alert/order register is a MUNICIPAL/EMCPA construct; a reserve has no
              such register (the request stands as submitted), so the selector is hidden for FN. */}
          {!isFirstNation && (
            <label className="block text-[11px] text-slate-400 space-y-1">
              <span>Level</span>
              <select
                data-testid="evac-level"
                value={level}
                onChange={(e) => setLevel(e.target.value as EvacOrderLevel)}
                className={inputCls}
              >
                {LEVEL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="grid grid-cols-2 gap-2">
            <label className="block text-[11px] text-slate-400 space-y-1">
              <span>{isFirstNation ? 'Jurisdiction type *' : 'Issuing authority type *'}</span>
              <select
                data-testid="evac-authority-type"
                value={authorityType}
                onChange={(e) => setAuthorityType(e.target.value as IssuingAuthorityType)}
                className={inputCls}
              >
                {ISSUING_AUTHORITY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {ISSUING_AUTHORITY_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-[11px] text-slate-400 space-y-1">
              {/* #637 — on a reserve there is no ISSUER; this names the affected First Nation
                  community (whose evacuation is requested), never an issuing body. */}
              <span>{isFirstNation ? 'First Nation community *' : 'Issuing body (named) *'}</span>
              <input
                data-testid="evac-authority-name"
                value={authorityName}
                autoFocus
                onChange={(e) => setAuthorityName(e.target.value)}
                placeholder={isFirstNation ? 'e.g. Kashechewan First Nation' : 'e.g. Township of Elliot Lake'}
                className={inputCls}
              />
            </label>
          </div>

          {/* #458 — OPTIONAL coordinating authority. Secondary to the issuer (§4.2
              coordination ≠ command); only the Far-North tripartite case populates it.
              #637 — hidden on the First Nation path: the PEOC-coordinates / ISC-included model
              is inherent to the request framing, not an optional add-on. */}
          {!isFirstNation && (
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-[11px] text-slate-400 space-y-1">
                <span>{COORDINATING_AUTHORITY_OPTIONAL_LABEL}</span>
                <select
                  data-testid="evac-coordinating-type"
                  value={coordType}
                  onChange={(e) => setCoordType(e.target.value as IssuingAuthorityType)}
                  className={inputCls}
                >
                  {ISSUING_AUTHORITY_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {ISSUING_AUTHORITY_TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-[11px] text-slate-400 space-y-1">
                <span>{COORDINATING_BODY_NAMED_LABEL}</span>
                <input
                  data-testid="evac-coordinating-name"
                  value={coordName}
                  onChange={(e) => setCoordName(e.target.value)}
                  placeholder="e.g. Provincial Emergency Operations Centre"
                  className={inputCls}
                />
              </label>
            </div>
          )}

          <label className="block text-[11px] text-slate-400 space-y-1">
            <span>Area name</span>
            <input
              data-testid="evac-area-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Kashechewan riverfront (optional)"
              className={inputCls}
            />
          </label>

          <label className="block text-[11px] text-slate-400 space-y-1">
            <span>Affected population (estimate)</span>
            <input
              type="number"
              min={0}
              step={1}
              data-testid="evac-population"
              value={population}
              onChange={(e) => setPopulation(e.target.value)}
              placeholder="—"
              className={inputCls}
            />
          </label>

          {/* #637 — on the First Nation path this free-text field captures the OPTIONAL
              evacuation-decision provenance (the Joint Command Call); it is never an "issuer". */}
          <label className="block text-[11px] text-slate-400 space-y-1">
            <span>{isFirstNation ? 'Evacuation decision & context (optional)' : 'Notes'}</span>
            <textarea
              data-testid="evac-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder={
                isFirstNation
                  ? 'e.g. Joint Command Call — Chief and Council with ISC; routes, receiving site'
                  : 'Optional context (routes, receiving site, …)'
              }
              className={inputCls}
            />
          </label>
        </div>

        <footer className="flex items-center gap-2 px-5 py-3 border-t border-slate-700">
          <button
            type="button"
            data-testid="evac-order-submit"
            disabled={busy}
            onClick={submit}
            className="text-xs py-1.5 px-3 rounded bg-violet-700/90 hover:bg-violet-700 border border-violet-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
          >
            {busy ? 'Recording…' : actionLabel}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-xs py-1.5 px-3 rounded bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-300"
          >
            Cancel
          </button>
        </footer>
      </div>
    </div>
  );
}
